import { useState, useEffect, useCallback, useRef } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ResponsiveContainer,
} from "recharts";
import { generateFakeTemperatureSeries } from "../fakeData";
import { captureChart, canvasToBlob, downloadBlob } from "../chartExport";
import { apiFetch } from "../apiFetch";
import PreDataNotice from "./PreDataNotice";
import {
  CHART_RANGE_FLOOR,
  RANGE_PRESETS,
  customRangeError,
  preDataNotice,
  windowBounds,
} from "../chartRange";

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const LINE_COLOR = "#ef4444";
// Anomaly thresholds (°C) — mirrors the detector's low/high limits.
const TEMP_MIN = 0;
const TEMP_MAX = 40;

function formatTimestamp(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

// The /api/sensors/:deviceId endpoint returns an array of historical records
// when a range is asked for (#200) and a single latest record when one is not.
// Both are normalised to an array, so this keeps rendering against a server that
// predates the range support rather than throwing.
function normalize(json) {
  if (Array.isArray(json)) return json;
  if (json && typeof json === "object" && !json.error) return [json];
  return [];
}

function readTemperature(item) {
  const raw = item?.payload?.temperature ?? item?.temperature;
  const val = parseFloat(raw);
  return Number.isFinite(val) ? val : null;
}

function Stat({ label, value, highlight }) {
  return (
    <div style={styles.stat}>
      <span style={styles.statLabel}>{label}</span>
      <span style={{ ...styles.statValue, color: highlight ? "#ef4444" : "#111827" }}>{value}</span>
    </div>
  );
}

// `simulated` renders from generated data and makes no network request — see
// the note on TowerDriftChart and fakeData.js.
export default function TemperatureChart({ deviceId, apiBaseUrl, label, t = (k) => k, simulated = false }) {
  const [range, setRange] = useState("7d");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [customError, setCustomError] = useState(null);
  const [committedCustom, setCommittedCustom] = useState({ from: "", to: "" });
  const [exportProgress, setExportProgress] = useState(null); // null = idle
  // What the API had to do to the series before sending it. A 30-day window on a
  // chatty sensor is thousands of rows, so it is decimated to a point budget and
  // may stop short of the start of the range. Both are stated rather than left
  // for the reader to infer from the shape of the line (#108, #200).
  const [series, setSeries] = useState({ truncated: false, bucketSize: 1 });
  // When this sensor started reporting. This endpoint returns a bare array, so
  // it travels as X-Readings-First rather than in the body (#184).
  const [firstReading, setFirstReading] = useState(null);

  const cardRef = useRef(null);

  useEffect(() => {
    const styleId = "export-keyframes";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
      document.head.appendChild(style);
    }
  }, []);

  // From the shared list so all four charts offer the same set (#231).
  const RANGE_OPTIONS = RANGE_PRESETS.map(({ key, value }) => ({ label: t(key), value }));

  const buildUrl = () => {
    const base = `${apiBaseUrl}/api/sensors/${encodeURIComponent(deviceId)}`;
    if (range === "custom" && committedCustom.from && committedCustom.to) {
      return `${base}?start=${committedCustom.from}&end=${committedCustom.to}`;
    }
    return `${base}?range=${range}`;
  };

  // Custom is selected but Apply has not been pressed on a complete pair of
  // dates yet, so there is no range to ask for. buildUrl() falls through to
  // `range=custom`, which this endpoint used to answer with the latest reading —
  // it ignored `range` entirely. Now that it honours it (#200) the same request
  // is a 400, and it would land on screen as "Failed to load" the instant the
  // button was clicked, before the user could type anything. TowerDriftChart hit
  // exactly this in #91; the guard is the same one, for the same reason.
  //
  // Falling back to a default range instead would be worse: it charts 7 days
  // while the user is looking at an empty Custom form.
  const customPending =
    range === "custom" && !(committedCustom.from && committedCustom.to);

  // Whether this window opens before the sensor existed. Relevant to the preset
  // ranges too, not just Custom: three devices came online in October, so a
  // plain 30-day window already starts before their first reading (#184).
  const windowStart = windowBounds(range, committedCustom);
  const preData = preDataNotice(firstReading, windowStart.from, windowStart.to);

  const fetchData = useCallback(async () => {
    if (customPending) { setError(null); setLoading(false); return; }
    try {
      let json;
      let meta = { truncated: false, bucketSize: 1, firstReading: null };
      if (simulated) {
        json = generateFakeTemperatureSeries(deviceId, range);
      } else {
        const res = await apiFetch(buildUrl());
        if (!res.ok) throw new Error(`API error ${res.status}`);
        // Optional-chained: a server from before #200 sends neither header, and
        // absent means "complete" and "not decimated" respectively.
        meta = {
          truncated: res.headers?.get("X-Readings-Truncated") === "true",
          bucketSize: parseInt(res.headers?.get("X-Readings-Bucket-Size"), 10) || 1,
          // Absent on a server from before #184, which is handled the same way
          // as the two above: absent means there is nothing to say.
          firstReading: res.headers?.get("X-Readings-First") ?? null,
        };
        json = await res.json();
      }
      setSeries(meta);
      // Simulated data describes no real deployment, so it must not claim an
      // install date.
      setFirstReading(simulated ? null : meta.firstReading);
      const points = normalize(json)
        .map((d) => ({ ts: d.SK ?? d.timestamp, temperature: readTemperature(d) }))
        .filter((p) => p.ts && p.temperature !== null)
        .sort((a, b) => new Date(a.ts) - new Date(b.ts));
      setData(points);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [deviceId, apiBaseUrl, range, committedCustom, customPending, simulated]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (customPending) { setLoading(false); return; }
    setLoading(true);
    fetchData();
    const interval = setInterval(fetchData, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchData, customPending]);

  const temps = data.map((d) => d.temperature);
  const minTemp = temps.length ? Math.min(...temps).toFixed(1) : "—";
  const maxTemp = temps.length ? Math.max(...temps).toFixed(1) : "—";
  const latestTemp = temps.length ? temps[temps.length - 1].toFixed(1) : "—";
  const latestOutOfRange =
    temps.length && (temps[temps.length - 1] < TEMP_MIN || temps[temps.length - 1] > TEMP_MAX);

  // ── Export handlers ───────────────────────────────────────────────────────
  const exportJpeg = async () => {
    if (exportProgress !== null) return;
    setExportProgress({ pct: 30, label: "Capturing chart…" });
    await new Promise((r) => setTimeout(r, 50)); // let UI paint
    try {
      const chartDiv = cardRef.current.querySelector(".recharts-wrapper");
      if (!chartDiv) { setExportProgress(null); return; }
      const canvas = await captureChart(chartDiv, "jpeg");
      setExportProgress({ pct: 80, label: "Saving image…" });
      await new Promise(r => setTimeout(r, 50));
      // Blob rather than a data: URL — see chartExport.js (#83).
      const blob = await canvasToBlob(canvas, "image/jpeg", 0.95);
      downloadBlob(blob, `${deviceId}_temperature_chart.jpg`);
      setExportProgress({ pct: 100, label: "Done!" });
      setTimeout(() => setExportProgress(null), 1200);
    } catch (e) {
      console.error(e);
      setExportProgress(null);
    }
  };

  const exportPdf = async () => {
    if (exportProgress !== null) return;
    try {
      setExportProgress({ pct: 10, label: "Preparing…" });
      await new Promise((r) => setTimeout(r, 50));

      const { jsPDF } = await import("jspdf");
      setExportProgress({ pct: 25, label: "Loading libraries…" });
      await new Promise((r) => setTimeout(r, 50));

      const chartDiv = cardRef.current.querySelector(".recharts-wrapper");
      if (!chartDiv) { setExportProgress(null); return; }

      setExportProgress({ pct: 45, label: "Capturing chart…" });
      await new Promise((r) => setTimeout(r, 50));

      // Budgeted scale, not a fixed 3× — see chartExport.js. This chart carried
      // the identical defect to the drift chart it was copied from.
      const canvas = await captureChart(chartDiv, "pdf");

      setExportProgress({ pct: 70, label: "Generating PDF…" });
      await new Promise((r) => setTimeout(r, 50));

      const pageW = 595.28; // A4 portrait pts
      const pageH = 841.89;
      const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });

      let y = 30;
      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(17, 24, 39);
      pdf.text(`Temperature Report — ${label ?? deviceId}`, 30, y);

      y += 18;
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(120, 120, 120);
      pdf.text(`Device ID: ${deviceId}`, 30, y);
      y += 14;
      pdf.text(`Exported: ${new Date().toLocaleString()}`, 30, y);
      y += 14;
      pdf.text(`Range: ${range === "custom" ? `${committedCustom.from} → ${committedCustom.to}` : range}`, 30, y);

      y += 14;
      pdf.setTextColor(40, 40, 40);
      pdf.text(
        `Min: ${minTemp}°C   |   Max: ${maxTemp}°C   |   Latest: ${latestTemp}°C   |   Thresholds: ${TEMP_MIN}–${TEMP_MAX}°C`,
        30, y
      );

      y += 20;
      pdf.setDrawColor(220, 220, 220);
      pdf.line(30, y, pageW - 30, y);
      y += 10;

      const availH = pageH - y - 20;
      const aspect = canvas.width / canvas.height;
      let chartW = pageW - 60;
      let chartH = chartW / aspect;
      if (chartH > availH) { chartH = availH; chartW = chartH * aspect; }
      const chartX = 30 + (pageW - 60 - chartW) / 2;

      pdf.addImage(canvas, "JPEG", chartX, y, chartW, chartH);

      setExportProgress({ pct: 90, label: "Saving…" });
      await new Promise((r) => setTimeout(r, 50));

      downloadBlob(pdf.output("blob"), `${deviceId}_temperature_report.pdf`);
      setExportProgress({ pct: 100, label: "Done!" });
      setTimeout(() => setExportProgress(null), 1200);
    } catch (e) {
      console.error("PDF export failed:", e);
      setExportProgress(null);
    }
  };

  // ── Sub-components ────────────────────────────────────────────────────────
  function TempTooltip({ active, payload, label: ttLabel }) {
    if (!active || !payload?.length) return null;
    const point = payload[0]?.payload;
    const outOfRange = point.temperature < TEMP_MIN || point.temperature > TEMP_MAX;
    return (
      <div style={styles.tooltip}>
        <p style={styles.tooltipTime}>{formatTimestamp(ttLabel)}</p>
        <p style={{ color: LINE_COLOR, margin: "2px 0" }}>
          Temperature: {point.temperature?.toFixed(1)}°C
        </p>
        {outOfRange && (
          <p style={{ color: "#f59e0b", marginTop: 4, fontSize: 12 }}>
            ⚠ {t("thresholdExceeded")}
          </p>
        )}
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div ref={cardRef} style={{ ...styles.card, maxWidth: 1600 }}>

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>{label ?? deviceId}</h2>
          <p style={styles.subtitle}>{deviceId}</p>
        </div>
        <div style={styles.headerRight}>
          <div style={styles.exportRow}>
            <button
              style={{ ...styles.exportBtn, opacity: exportProgress !== null ? 0.5 : 1 }}
              onClick={exportJpeg}
              disabled={exportProgress !== null}
            >
              ⬇ JPEG
            </button>
            <button
              style={{ ...styles.exportBtn, opacity: exportProgress !== null ? 0.5 : 1 }}
              onClick={exportPdf}
              disabled={exportProgress !== null}
            >
              ⬇ PDF
            </button>
          </div>
          {exportProgress !== null && (
            <div style={styles.progressWrap}>
              <div style={styles.spinner} />
              <span style={styles.progressLabel}>{exportProgress.label}</span>
            </div>
          )}
        </div>
      </div>

      {/* Range selector */}
      <div style={styles.rangeRow}>
        {RANGE_OPTIONS.map((opt) => (
          <button key={opt.value} onClick={() => setRange(opt.value)}
            style={range === opt.value ? styles.rangeActive : styles.rangeBtn}>
            {opt.label}
          </button>
        ))}
        <button onClick={() => setRange("custom")}
          style={range === "custom" ? styles.rangeActive : styles.rangeBtn}>
          {t("rangeCustom")}
        </button>
        <span style={styles.lastUpdated}>
          {lastUpdated ? `${t("updated")} ${lastUpdated.toLocaleTimeString()}` : t("loading")}
        </span>
      </div>

      {/* Custom date range picker */}
      {range === "custom" && (
        <div style={styles.customRangeRow}>
          <label style={styles.customLabel}>{t("customFrom")}</label>
          <input type="date" value={customFrom}
            min={CHART_RANGE_FLOOR}
            max={customTo || new Date().toISOString().split("T")[0]}
            onChange={(e) => { setCustomFrom(e.target.value); setCustomError(null); }}
            style={styles.dateInput}
          />
          {/* One click back to the start of the data (#231). Sets the field and
              stops there rather than applying, so a long window is never
              requested before the operator can see what it chose. */}
          <button type="button" style={styles.fromStartBtn}
            title={t("fromStartTitle")}
            onClick={() => { setCustomFrom(CHART_RANGE_FLOOR); setCustomError(null); }}>
            {t("fromStart")}
          </button>
          <label style={styles.customLabel}>{t("customTo")}</label>
          <input type="date" value={customTo} min={customFrom || CHART_RANGE_FLOOR}
            max={new Date().toISOString().split("T")[0]}
            onChange={(e) => { setCustomTo(e.target.value); setCustomError(null); }}
            style={styles.dateInput}
          />
          <button style={styles.applyBtn} onClick={() => {
            const key = customRangeError(customFrom, customTo);
            if (key) { setCustomError(t(key)); return; }
            setCustomError(null);
            setCommittedCustom({ from: customFrom, to: customTo });
          }}>
            {t("apply")}
          </button>
          {customError && <span style={styles.customError}>{customError}</span>}
        </div>
      )}

      {/* Summary stats */}
      {data.length > 0 && (
        <div style={styles.statsRow}>
          <Stat label="Min" value={`${minTemp}°C`} />
          <Stat label="Max" value={`${maxTemp}°C`} />
          <Stat label="Latest" value={`${latestTemp}°C`} highlight={latestOutOfRange} />
        </div>
      )}

      {/* Why the chart is blank before a given date, when it is. Not gated on
          data.length: the case most in need of explaining is the empty one. */}
      {!loading && !error && !customPending && (
        <PreDataNotice notice={preData} t={t} />
      )}

      {/* What the server did to the series before sending it */}
      {!loading && !error && (series.truncated || series.bucketSize > 1) && (
        <div style={styles.seriesNote}>
          {series.bucketSize > 1 && (
            <span>
              Averaged: each point is the mean of about {series.bucketSize} readings.{" "}
            </span>
          )}
          {series.truncated && (
            <span>
              This window was too large to read in full, so the chart starts later
              than the range you asked for.
            </span>
          )}
        </div>
      )}

      {/* Chart */}
      {loading ? (
        <div style={styles.placeholder}>{t("driftChartLoading")}</div>
      ) : error ? (
        <div style={styles.errorBox}>{t("driftChartError")}: {error}</div>
      ) : data.length === 0 ? (
        <div style={styles.placeholder}>{t("driftChartNoData")}</div>
      ) : (
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="ts"
              tickFormatter={(v) => new Date(v).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
              tick={{ fontSize: 11, fill: "#6b7280" }} minTickGap={40}
            />
            <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} tickFormatter={(v) => `${v}°`}
              domain={["auto", "auto"]}
              label={{ value: "Temperature (°C)", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "#9ca3af" } }}
            />
            <Tooltip content={<TempTooltip />} />
            <ReferenceLine y={TEMP_MIN} stroke="#3b82f6" strokeDasharray="6 3"
              label={{ value: `${TEMP_MIN}°C`, position: "insideBottomRight", fontSize: 11, fill: "#3b82f6" }}
            />
            <ReferenceLine y={TEMP_MAX} stroke="#ef4444" strokeDasharray="6 3"
              label={{ value: `${TEMP_MAX}°C`, position: "insideTopRight", fontSize: 11, fill: "#ef4444" }}
            />
            <Line type="monotone" dataKey="temperature" stroke={LINE_COLOR} dot={false} strokeWidth={2.5} activeDot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

const styles = {
  card: { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "20px 24px", fontFamily: "'Inter', system-ui, sans-serif" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 },
  headerRight: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 },
  exportRow: { display: "flex", gap: 6 },
  exportBtn: { padding: "4px 12px", border: "1px solid #d1d5db", borderRadius: 6, background: "#f9fafb", color: "#374151", cursor: "pointer", fontSize: 12, fontWeight: 600 },
  progressWrap: { display: "flex", alignItems: "center", gap: 10, marginTop: 8 },
  progressLabel: { fontSize: 11, color: "#6b7280", whiteSpace: "nowrap", minWidth: 110 },
  spinner: { width: 16, height: 16, borderRadius: "50%", border: "2px solid #e5e7eb", borderTopColor: "#3b82f6", animation: "spin 0.8s linear infinite", flexShrink: 0 },
  title: { margin: 0, fontSize: 18, fontWeight: 600, color: "#111827" },
  subtitle: { margin: "2px 0 0", fontSize: 12, color: "#9ca3af" },
  rangeRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 16 },
  rangeBtn: { padding: "4px 12px", border: "1px solid #d1d5db", borderRadius: 6, background: "#f9fafb", color: "#374151", cursor: "pointer", fontSize: 13 },
  rangeActive: { padding: "4px 12px", border: "1px solid #3b82f6", borderRadius: 6, background: "#eff6ff", color: "#1d4ed8", cursor: "pointer", fontSize: 13, fontWeight: 600 },
  lastUpdated: { marginLeft: "auto", fontSize: 12, color: "#9ca3af" },
  seriesNote: { marginBottom: 12, padding: "8px 12px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, color: "#92400e", fontSize: 12, lineHeight: 1.5 },
  statsRow: { display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 16, padding: "12px 16px", background: "#f9fafb", borderRadius: 6 },
  stat: { display: "flex", flexDirection: "column", gap: 2 },
  statLabel: { fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em" },
  statValue: { fontSize: 16, fontWeight: 600 },
  placeholder: { height: 320, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 14 },
  errorBox: { height: 80, display: "flex", alignItems: "center", justifyContent: "center", color: "#ef4444", fontSize: 14, background: "#fef2f2", borderRadius: 6 },
  tooltip: { background: "#1f2937", border: "none", borderRadius: 6, padding: "10px 14px", color: "#f9fafb", fontSize: 13 },
  tooltipTime: { margin: "0 0 6px", color: "#9ca3af", fontSize: 12 },
  customRangeRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" },
  customLabel: { fontSize: 13, color: "#6b7280", fontWeight: 500 },
  dateInput: { padding: "4px 8px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, color: "#374151", background: "#f9fafb" },
  applyBtn: { padding: "4px 14px", border: "1px solid #3b82f6", borderRadius: 6, background: "#eff6ff", color: "#1d4ed8", cursor: "pointer", fontSize: 13, fontWeight: 600 },
  // Quieter than applyBtn on purpose: this fills a field, it does not run the
  // query, and it must not compete with Apply for the eye (#231).
  fromStartBtn: { padding: "4px 10px", border: "1px solid #d1d5db", borderRadius: 6, background: "#f9fafb", color: "#6b7280", cursor: "pointer", fontSize: 12 },
  customError: { fontSize: 12, color: "#ef4444" },
};
